import { object, string, array, type InferType, type TestContext } from 'yup';
import type { RoundTripValue } from '@/components/tripBookingPage/tripBooking';
import { type AssistiveDevice, Locale } from '@/types';

interface WheelChairI18nKeys {
  firstWheelChairs: string;
  lastWheelChair: string;
}

function wheelChairsI18nReducerFactory(
  locale: Locale,
): (
    props: WheelChairI18nKeys,
    item: AssistiveDevice,
    index: number,
    allDevices: AssistiveDevice[],
  ) => WheelChairI18nKeys {
  return (props, item, index, allDevices) => {
    if (index + 1 === allDevices.length) {
      props.lastWheelChair = `"${item[locale]}"`;
      return props;
    }
    props.firstWheelChairs += `"${item[locale]}"${index + 2 < allDevices.length ? ', ' : ''} `;
    return props;
  };
}

/**
 * Retourne le schéma de validation pour les aides à la mobilité.
 */
export const getAssistiveDevicesSchema = (selectedDevices: RoundTripValue<AssistiveDevice[]>, locale: Locale) =>
  object({
    assistiveDevices: object({
      outward: array()
        .of(string().defined())
        .defined()
        .nullable()
        .test('hasMaxOneWheelchair', (value, context) =>
          testMaxOneWheelchair(selectedDevices.outward, locale, context))
        .nullable(),
      inward: array()
        .of(string().defined())
        .defined()
        .nullable()
        .test('hasMaxOneWheelchair', (value, context) => testMaxOneWheelchair(selectedDevices.inward, locale, context)),
    }),
    roundTripSameDevices: string().nonNullable().default('true').oneOf(['true', 'false']), // propriété interne mais essentielle au bon fonctionnement des équipements pour un déplacement aller-retour.
  }).default({
    assistiveDevices: {
      // La valeur nulle est définie lorsque l'utilisateur n'a jamais passé dans l'étape avant.
      //  Comme les équipements sont optionnels, il peut alors décider de ne rien sélectionner,
      // ce qui donnerait alors une liste vide.
      outward: null,
      inward: null,
    },
    roundTripSameDevices: 'true',

  });

function testMaxOneWheelchair(selectedDevices: AssistiveDevice[], locale: Locale, context: TestContext) {
  const selectedWheelchairs = selectedDevices.filter((item) => item.type === 'wheelchair');
  if (selectedWheelchairs.length <= 1) {
    return true;
  }

  const wheelChairsI18nOptions = selectedWheelchairs.reduce<WheelChairI18nKeys>(wheelChairsI18nReducerFactory(locale), {
    firstWheelChairs: '',
    lastWheelChair: '',
  });

  return context.createError({
    message: () => ({
      i18nKey: 'pages.tripBooking.forms.items.assistiveDevices.validations.tooMuchWheelChairs',
      options: { ...wheelChairsI18nOptions, count: selectedWheelchairs.length },
    }),
  });
}

export type AssistiveDeviceSchema = InferType<ReturnType<typeof getAssistiveDevicesSchema>>;
